import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';

const IntegrationsTab = () => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const integrations = [
    {
      id: 1,
      name: "Active Directory",
      type: "Authentication",
      description: "Single sign-on and user management integration",
      status: "Connected",
      lastSync: "2025-09-19 10:15:00",
      config: {
        server: "ad.cyberguard.gov",
        port: "389",
        baseDN: "DC=cyberguard,DC=gov"
      },
      icon: "Users"
    },
    {
      id: 2,
      name: "Jira Service Management",
      type: "Ticketing",
      description: "Incident ticket creation and tracking",
      status: "Connected",
      lastSync: "2025-09-19 09:30:00",
      config: {
        url: "https://cyberguard.atlassian.net",
        project: "INCIDENT",
        apiKey: "••••••••••••••••"
      },
      icon: "Ticket"
    },
    {
      id: 3,
      name: "Asset Management System",
      type: "Asset Discovery",
      description: "Automatic asset discovery and inventory",
      status: "Disconnected",
      lastSync: "2025-09-18 14:20:00",
      config: {
        endpoint: "https://assets.cyberguard.gov/api",
        apiKey: "••••••••••••••••"
      },
      icon: "Server"
    },
    {
      id: 4,
      name: "SAML Identity Provider",
      type: "Authentication",
      description: "SAML-based single sign-on",
      status: "Connected",
      lastSync: "2025-09-19 11:00:00",
      config: {
        entityId: "cyberguard-saml",
        ssoUrl: "https://sso.cyberguard.gov/saml",
        certificate: "••••••••••••••••"
      },
      icon: "Shield"
    },
    {
      id: 5,
      name: "Slack Workspace",
      type: "Notifications",
      description: "Real-time incident notifications",
      status: "Connected",
      lastSync: "2025-09-19 11:25:00",
      config: {
        workspace: "cyberguard-team",
        webhook: "••••••••••••••••",
        channel: "#incidents"
      },
      icon: "MessageSquare"
    },
    {
      id: 6,
      name: "Email Gateway",
      type: "Notifications",
      description: "SMTP email notifications",
      status: "Connected",
      lastSync: "2025-09-19 11:20:00",
      config: {
        server: "smtp.cyberguard.gov",
        port: "587",
        encryption: "TLS"
      },
      icon: "Mail"
    }
  ];

  const notificationChannels = [
    {
      id: 1,
      name: "Critical Incidents",
      description: "High priority incident notifications",
      channels: ["Email", "Slack", "SMS"],
      enabled: true
    },
    {
      id: 2,
      name: "System Alerts",
      description: "System health and performance alerts",
      channels: ["Email", "Slack"],
      enabled: true
    },
    {
      id: 3,
      name: "Backup Reports",
      description: "Daily backup status reports",
      channels: ["Email"],
      enabled: true
    },
    {
      id: 4,
      name: "Security Updates",
      description: "Security patch and update notifications",
      channels: ["Email"],
      enabled: false
    }
  ];

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'Connected':
        return `${baseClasses} bg-success text-success-foreground`;
      case 'Disconnected':
        return `${baseClasses} bg-error text-error-foreground`;
      case 'Syncing':
        return `${baseClasses} bg-warning text-warning-foreground`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Authentication':
        return 'text-primary';
      case 'Ticketing':
        return 'text-accent';
      case 'Asset Discovery':
        return 'text-secondary';
      case 'Notifications':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const handleConfigureIntegration = (integration) => {
    setSelectedIntegration(integration);
    setShowConfigModal(true);
  };

  const handleTestConnection = (integrationId) => {
    console.log('Testing connection for integration:', integrationId);
  };

  const handleToggleIntegration = (integrationId) => {
    console.log('Toggling integration:', integrationId);
  };

  return (
    <div className="space-y-8">
      {/* Integration Overview */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Plug" size={20} className="mr-2" />
              Third-Party Integrations
            </h3>
            <p className="text-sm text-muted-foreground">Manage external system connections and data synchronization</p>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left">
            Add Integration
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations?.map((integration) => (
            <div key={integration?.id} className="p-4 border border-border rounded-lg hover:bg-muted/50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-muted ${getTypeColor(integration?.type)}`}>
                    <Icon name={integration?.icon} size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{integration?.name}</h4>
                    <p className="text-xs text-muted-foreground">{integration?.type}</p>
                  </div>
                </div>
                <span className={getStatusBadge(integration?.status)}>
                  {integration?.status}
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">{integration?.description}</p>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                <span>Last sync: {new Date(integration.lastSync)?.toLocaleString()}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  iconName="Settings"
                  onClick={() => handleConfigureIntegration(integration)}
                />
                <Button 
                  variant="ghost" 
                  size="sm" 
                  iconName="Zap"
                  onClick={() => handleTestConnection(integration?.id)}
                />
                <Button 
                  variant="ghost" 
                  size="sm" 
                  iconName={integration?.status === 'Connected' ? 'Pause' : 'Play'}
                  onClick={() => handleToggleIntegration(integration?.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Notification Channels */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Bell" size={20} className="mr-2" />
              Notification Channels
            </h3>
            <p className="text-sm text-muted-foreground">Configure multi-channel notification delivery</p>
          </div>
          <Button variant="outline" iconName="Settings" iconPosition="left">
            Channel Settings
          </Button>
        </div>

        <div className="space-y-4">
          {notificationChannels?.map((channel) => (
            <div key={channel?.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-foreground">{channel?.name}</h4>
                    <Checkbox
                      checked={channel?.enabled}
                      onChange={() => console.log('Toggle channel:', channel?.id)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{channel?.description}</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-muted-foreground">Channels:</span>
                    {channel?.channels?.map((ch, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-muted rounded-full text-foreground">
                        {ch}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" iconName="Edit" />
                  <Button variant="ghost" size="sm" iconName="Send" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Sync Status */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="RefreshCw" size={20} className="mr-2" />
              Synchronization Status
            </h3>
            <p className="text-sm text-muted-foreground">Monitor data synchronization across integrations</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left">
            Sync All
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-foreground">Integration</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Last Sync</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Records</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {integrations?.filter(i => i?.status === 'Connected')?.map((integration) => (
                <tr key={integration?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <Icon name={integration?.icon} size={16} className="text-muted-foreground" />
                      <span className="font-medium text-foreground">{integration?.name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={getStatusBadge('Connected')}>
                      Synced
                    </span>
                  </td>
                  <td className="p-4 text-sm text-foreground">
                    {new Date(integration.lastSync)?.toLocaleString()}
                  </td>
                  <td className="p-4 text-sm text-foreground">
                    {Math.floor(Math.random() * 1000) + 100} records
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" iconName="RefreshCw" />
                      <Button variant="ghost" size="sm" iconName="Eye" />
                      <Button variant="ghost" size="sm" iconName="Download" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Configuration Modal */}
      {showConfigModal && selectedIntegration && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-medium w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Configure {selectedIntegration?.name}
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                iconName="X"
                onClick={() => setShowConfigModal(false)}
              />
            </div>
            <div className="p-6 space-y-4">
              {Object.entries(selectedIntegration?.config)?.map(([key, value]) => (
                <Input
                  key={key}
                  label={key?.charAt(0)?.toUpperCase() + key?.slice(1)}
                  type={key?.includes('password') || key?.includes('key') || key?.includes('certificate') ? 'password' : 'text'}
                  value={value}
                  onChange={() => {}}
                />
              ))}
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setShowConfigModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="default">
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsTab;